
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Settings, Eye } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery } from "convex/react";
import { api } from "../../api";
import { type GenericId as Id } from "convex/values";

interface App {
  id: string;
  bundle_id: string;
  app_name: string;
}

interface AppScopeTabProps {
  app: App;
}

interface LevelScope {
  _id: string;
  name: string;
  description?: string;
  private: boolean;
  _creationTime: number;
  allowedApps: string[];
  blockedApps: string[];
  appFilter: Record<string, any>;
  restrictions: string[];
}

export const AppScopeTab = ({ app }: AppScopeTabProps) => {
  // First, find the Convex app ID by searching for the bundle ID
  const appsQuery = useQuery(api.apps.list.all, {
    paginationOpts: {
      cursor: null,
      numItems: 1000,
    },
    query: app.bundle_id,
  });

  // Find the matching app from the query results
  const convexApp = appsQuery?.page.find(
    (appItem) => appItem.bundleId === app.bundle_id
  );

  // Only query levels if we have the Convex app ID
  const levelsData = useQuery(
    api.levels.list.withApp,
    convexApp ? {
      app: convexApp.id,
      paginationOpts: {
        cursor: null,
        numItems: 100,
      },
    } : "skip"
  );

  const isLoadingApps = appsQuery === undefined;
  const isLoadingLevels = convexApp && levelsData === undefined;
  const isLoading = isLoadingApps || isLoadingLevels;
  const levelScopes = (levelsData as any)?.page ?? [];

  const handleViewLevel = (levelId: string) => {
    console.log('View level:', levelId);
    // TODO: Navigate to level detail view
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading scope information...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span>Level Scope</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {levelScopes.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-2">
                  <Settings className="w-8 h-8 mx-auto" />
                </div>
                <p className="text-gray-500">
                  This app is not configured in any levels yet.
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Add this app to level configurations to control device access.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  This app is configured in {levelScopes.length} level(s).
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Level Name</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {levelScopes.map((level: LevelScope) => (
                      <TableRow key={level._id}>
                        <TableCell className="font-medium">
                          {level.name}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewLevel(level._id)}
                            className="flex items-center gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            View Level
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
